import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, ArrowLeft, Share2, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { marked } from 'marked';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

const ArticoloSingolo = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      // Prova prima negli articoli SEO
      let res = await fetch(`${API_URL}/api/seo-articles/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setArticle(data.article);
        fetchRelated(data.article);
        return;
      }

      // Altrimenti prova negli articoli blog
      res = await fetch(`${API_URL}/api/blog-articles/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setArticle(data.article);
        fetchRelated(data.article);
      }
    } catch (error) {
      console.error('Errore caricamento articolo:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelated = async (currentArticle) => {
    try {
      const res = await fetch(`${API_URL}/api/seo-articles`);
      if (res.ok) {
        const data = await res.json();
        // Filtra articoli correlati (stesso topic)
        const related = (data.articles || [])
          .filter(a => a.id !== currentArticle.id)
          .slice(0, 3);
        setRelatedArticles(related);
      }
    } catch (error) {
      console.error('Errore articoli correlati:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const [shareMessage, setShareMessage] = useState('');

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = article?.title || 'Articolo PRIME VISION';
    const shareText = article?.excerpt || article?.meta_description || '';

    // Prova Web Share API (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.log('Share error:', error);
        }
      }
    }
    
    // Fallback: copia URL negli appunti
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage('Link copiato negli appunti!');
      setTimeout(() => setShareMessage(''), 3000);
    } catch (error) {
      // Fallback per browser vecchi
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShareMessage('Link copiato!');
      setTimeout(() => setShareMessage(''), 3000);
    }
  };

  // Parse content: if it contains markdown syntax, convert it
  const getRenderedContent = (content) => {
    if (!content) return '';
    // Check if content looks like markdown (contains ## or ** or |---)
    const isMarkdown = /^#{1,6}\s|^\*\*|\|[-:]+\||^\-\s|\n#{1,6}\s/m.test(content);
    if (isMarkdown) {
      return marked.parse(content);
    }
    // Already HTML
    return content;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A9FCB] mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento articolo...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Articolo non trovato</h1>
          <Link to="/blog" className="text-[#2A9FCB] hover:underline">
            Torna al blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <Helmet>
        <title>{article.meta_title || article.title} | PRIME VISION</title>
        <meta name="description" content={article.meta_description || ''} />
        <meta name="keywords" content={article.keywords || ''} />
        <link rel="canonical" href={`https://primevision.it/articolo/${slug}`} />
        
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description || ''} />
        <meta property="og:url" content={`https://primevision.it/articolo/${slug}`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5]">
        {/* Hero */}
        <section style={{background: 'linear-gradient(135deg, #2c4667 0%, #3d5a80 100%)'}} className="text-white py-16">
          <div className="container mx-auto px-4">
            <Link 
              to="/blog" 
              className="inline-flex items-center mb-6 bg-white/60 hover:bg-white/80 px-4 py-2 rounded-full transition-colors text-[#2c4667]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span className="font-semibold">Torna al Blog</span>
            </Link>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold max-w-4xl text-white">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-6" style={{ color: '#ffffff' }}>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: '#ffffff' }} />
                <span className="font-semibold" style={{ color: '#ffffff' }}>{formatDate(article.created_at || article.published_at)}</span>
              </div>
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 hover:opacity-80 transition-colors"
                style={{ color: '#ffffff' }}
              >
                <Share2 className="w-5 h-5" />
                {shareMessage || 'Condividi'}
              </button>
            </div>
          </div>
        </section>

        {/* Contenuto */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Articolo principale */}
              <article className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm p-8 md:p-12">
                  {article.meta_description && (
                    <p className="text-xl text-gray-600 mb-8 pb-8 border-b">
                      {article.meta_description}
                    </p>
                  )}
                  
                  <div 
                    className="prose prose-lg max-w-none prose-headings:text-[#2c4667] prose-a:text-[#2A9FCB] prose-table:border-collapse prose-td:border prose-td:border-gray-200 prose-td:px-4 prose-td:py-2 prose-th:border prose-th:border-gray-200 prose-th:px-4 prose-th:py-2 prose-th:bg-gray-50"
                    dangerouslySetInnerHTML={{ __html: getRenderedContent(article.content) }}
                  />
                  
                  {article.keywords && (
                    <div className="mt-8 pt-8 border-t">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Tag className="w-5 h-5 text-gray-400" />
                        {article.keywords.split(',').map((kw, i) => (
                          <span 
                            key={i}
                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                          >
                            {kw.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div style={{background: 'linear-gradient(135deg, #2c4667 0%, #3d5a80 100%)'}} className="rounded-xl p-8 mt-8 text-white text-center">
                  <h3 className="text-2xl font-bold mb-4">
                    Hai bisogno di una visita oculistica?
                  </h3>
                  <p className="text-white/90 mb-6">
                    I nostri specialisti sono pronti ad aiutarti. Prenota ora la tua visita.
                  </p>
                  <Link to="/prenota">
                    <Button size="lg" className="bg-white text-[#2c4667] hover:bg-gray-100">
                      Prenota Visita
                    </Button>
                  </Link>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                {/* Articoli correlati */}
                {relatedArticles.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Articoli Correlati
                    </h3>
                    <div className="space-y-4">
                      {relatedArticles.map((related, i) => (
                        <Link
                          key={i}
                          to={`/articolo/${related.slug || related.id}`}
                          className="block group"
                        >
                          <h4 className="text-gray-800 group-hover:text-[#2A9FCB] font-medium line-clamp-2">
                            {related.title}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(related.created_at)}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Box contatti */}
                <div className="bg-[#E8F4F8] rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#2c4667] mb-4">
                    Contattaci
                  </h3>
                  <p className="text-[#3d5a80] text-sm mb-4">
                    Per domande o per prenotare una visita, siamo a tua disposizione.
                  </p>
                  <div className="space-y-2 text-sm">
                    <p className="text-[#2c4667]">
                      <strong>Tel:</strong> +39 02 1234567
                    </p>
                    <p className="text-[#2c4667]">
                      <strong>Email:</strong> info@primevision.it
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default ArticoloSingolo;
